import { supabase } from "@/integrations/supabase/client";
import { invokeWithSessionRetry } from "@/lib/invoke-with-session-retry";
import { LS_KEYS } from "@/lib/local-storage-keys";
import { createSignal } from "@/lib/feedback-signals";
import { buildAiIdempotencyKey, deductBudget, ensureTokenBudget, toFunctionErrorMessage, type AiBudgetOptions, type AiUsageSnapshot } from "@/lib/ai-client-common";

export type ExplainLevel = "quick" | "detailed" | "eli5" | "step_by_step";

export type ExplainRequest = {
  text: string;
  pageContext?: string;
  focus?: string;
  level: ExplainLevel;
  resourceId: string;
  pageNumber: number;
  language: "ar" | "en" | "mixed";
  idempotencyKey?: string;
};

export type ExplainResponse = {
  explanation: string;
  explanationId: string;
  fromCache: boolean;
  usage?: AiUsageSnapshot;
};

export type ExplanationEntry = {
  id: string;
  selection_text: string;
  level: ExplainLevel;
  language: string;
  explanation: string;
  page_number: number;
  from_cache: boolean;
  created_at: string;
};

export type FeedbackPayload = {
  thumbsUp?: boolean;
  difficulty?: "too_simple" | "just_right" | "too_complex";
  requestedExample?: boolean;
  requestedAnalogy?: boolean;
  feedbackText?: string;
};

export type ExplainLanguage = ExplainRequest["language"];

export function getStoredExplainLanguage(): ExplainLanguage | null {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(LS_KEYS.LANG_PREF);
  if (stored === "ar" || stored === "en" || stored === "mixed") return stored;
  return null;
}

export function setStoredExplainLanguage(language: ExplainLanguage): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LS_KEYS.LANG_PREF, language);
}

export async function requestExplanation(req: ExplainRequest, options?: AiBudgetOptions): Promise<ExplainResponse> {
  if (typeof window !== "undefined") {
    const storedLang = getStoredExplainLanguage();
    if (!storedLang && !req.language) {
      throw new Error("language_pref_required");
    }
  }

  ensureTokenBudget(options, 1500);

  const idempotencyKey = req.idempotencyKey ?? buildAiIdempotencyKey({
    feature: "explain",
    resourceId: req.resourceId,
    pageNumber: req.pageNumber,
    level: req.level,
    language: req.language,
    text: req.text.trim(),
    pageContext: req.pageContext ?? "",
    focus: req.focus ?? "",
  });

  const { data, error } = await invokeWithSessionRetry<ExplainResponse>("explain-text", {
    body: { ...req, idempotencyKey },
  });
  if (error || !data) {
    throw new Error(await toFunctionErrorMessage(error, "Failed to get explanation"));
  }

  deductBudget(options, 1500);
  return data as ExplainResponse;
}

export async function loadExplanationHistory(resourceId: string, userId: string): Promise<ExplanationEntry[]> {
  const { data, error } = await supabase
    .from("explanations")
    .select("id, selection_text, level, language, explanation, page_number, from_cache, created_at")
    .eq("resource_id", resourceId)
    .eq("user_id", userId)
    .order("created_at", { ascending: true })
    .limit(50);

  if (error) return [];
  return (data ?? []) as ExplanationEntry[];
}

export async function submitExplanationFeedback(
  explanationId: string,
  userId: string,
  resourceId: string,
  feedback: FeedbackPayload,
  faculty?: string | null,
): Promise<void> {
  await supabase.from("explanation_feedback").upsert(
    {
      explanation_id: explanationId,
      user_id: userId,
      thumbs_up: feedback.thumbsUp ?? null,
      difficulty: feedback.difficulty ?? null,
      requested_example: feedback.requestedExample ?? false,
      requested_analogy: feedback.requestedAnalogy ?? false,
      feedback_text: feedback.feedbackText ?? null,
    },
    { onConflict: "explanation_id,user_id" },
  );

  if (feedback.thumbsUp === true) {
    await createSignal({
      studentId: userId,
      resourceId,
      signalType: "positive",
      source: "feedback",
      faculty,
      rawFeedback: feedback.feedbackText ?? null,
    });
  } else if (feedback.thumbsUp === false) {
    await createSignal({
      studentId: userId,
      resourceId,
      signalType: "confusion",
      source: "feedback",
      faculty,
      rawFeedback: feedback.feedbackText ?? null,
    });
  }
}
